import { Add, Delete, PersonAdd, PersonRemove, Save } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import CloseRoundedIcon from '@mui/icons-material/Close'
import GroupsIcon from '@mui/icons-material/Groups'
import HubIcon from '@mui/icons-material/Hub'
import { LoadingButton } from '@mui/lab'
import {
    Autocomplete, Avatar,
    Badge,
    Box, Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider, IconButton,
    Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, useTheme
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import algo from '../../core/Algo'
import { IUserRole } from '../../definitions/Permissions'
import { IUser } from '../../definitions/Users'
import Helper from '../../helpers/Helper'
import PermissionManager from '../../managers/PermissionManager'
import UsersManager from '../../managers/UsersManager'
import { APPROVAL_ENTITLEMENTS } from '../../os_definitions/OsPermissions'
import { useAppDispatch, useAppSelector } from '../../store/Hooks'
import { changeRoute, setAllUserRoles } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { modals } from '../services/ModalService'
import { snackbar } from '../services/SnackbarService'

const GroupsEditor = () => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const routeParams = useAppSelector<{ roleUuid?: string, isClone: boolean }>(state => state.session.routeParams)

    // const existingRole = routeParams?.roleUuid // HERE CHANGE IT!

    const { status: roleStatus, response: group } = useQuery<IUserRole>({
        queryKey: buildQueryKey('userRole'),
        queryFn: () => ApiClient.getGroup(routeParams?.roleUuid),
        cache: false
    })

    const isClone = routeParams?.isClone
    const [groupName, setGroupName] = useState(group?.name ?? '')
    const [description, setDescription] = useState(group?.description ?? '')
    const [allowedEntitlements, setAllowedEntitlements] = useState(group?.entitlements?.allow ?? [])
    const isDefaultRole = false
    const disallowedEntitlements = []
    const [allowedAutocompleteKey, setAllowedAutocompleteKey] = useState(0)
    const [isAddMembersModalOpen, setIsAddMembersModalOpen] = useState(false)
    const [selectedEmails, setSelectedEmails] = useState<string[]>([])
    const [currentEmailInput, setCurrentEmailInput] = useState('')
    const [currentRoleUUID, setCurrentRoleUUID] = useState<string>(group?.uuid ?? '')
    const [selectedUserToRemove, setSelectedUserToRemove] = useState<string>(null)
    const [currentRoleKey, setCurrentRoleKey] = useState<string>(group?.key ?? '')
    const isEditMode = !!group || !!currentRoleUUID
    const isNewRole = !group && !currentRoleUUID

    const invalidateUsers = useInvalidate({ queryKey: buildQueryKey('allUsersByRole' + (currentRoleKey ?? group?.key ?? '')) })
    const invalidateAllGroups = useInvalidate({ queryKey: buildQueryKey('get-approvals-groups') })

    useEffect(() => {
        if (algo.hasVal(group) && roleStatus === 'success') {
            setGroupName(group?.name ?? '')
            setDescription(group?.description ?? '')
            setAllowedEntitlements(group?.entitlements?.allow ?? [])
            setCurrentRoleUUID(group?.uuid ?? '')
            setCurrentRoleKey(group?.key ?? '')
        }
    }, [roleStatus, group])

    const { execute: executeSaveRole, status: statusSaveRole } = useCommand({
        commandFn: async ({ groupName, description, allowedEntitlements, selectedEmails }: {
            groupName: string
            description: string
            allowedEntitlements: string[]
            selectedEmails: string[]
        }) => {
            let updatedGroups: IUserRole[] = []
            let newGroup: IUserRole | undefined

            if (currentRoleUUID) {
                updatedGroups = await PermissionManager.updateGroup(
                    currentRoleUUID,
                    groupName,
                    description,
                    isDefaultRole,
                    allowedEntitlements,
                    disallowedEntitlements
                )
            } else {
                updatedGroups = await PermissionManager.createGroup(
                    groupName,
                    description,
                    isDefaultRole,
                    allowedEntitlements,
                    disallowedEntitlements
                )

                newGroup = updatedGroups.find(role => role.name === groupName)
                if (newGroup) {
                    setCurrentRoleUUID(newGroup.uuid)
                    setCurrentRoleKey(newGroup.key)
                }
            }

            const roleUUID = currentRoleUUID || newGroup?.uuid
            if (roleUUID && selectedEmails.length > 0) {
                await ApiClient.assignUsersToRole(roleUUID, selectedEmails)
            }

            return { updatedGroups, newGroup }
        },
        onFinish: result => {
            if (result.status === 'success') {
                invalidateAllGroups()
                if (result.response.updatedGroups?.length > 0) {
                    dispatch(setAllUserRoles(result.response.updatedGroups))
                }
                snackbar.show({
                    severity: 'success',
                    text: currentRoleUUID ? 'Group updated successfully!' : 'Group created successfully!'
                })
                setSelectedEmails([])
            } else if (result.status === 'error') {
                snackbar.show({
                    severity: 'error',
                    text: `Failed to save the group. ${result.errorText}`
                })
            }
        }
    })

    const { execute: executeDeleteRole, status: statusDeleteRole } = useCommand({
        commandFn: async (roleUUID: string) => {
            if (routeParams?.isClone) {
                dispatch(changeRoute({ route: 'groups-management' }))
                return
            }
            return await PermissionManager.deleteGroup(roleUUID)
        },
        onFinish: result => {
            if (routeParams.isClone) {
                return
            }
            if (result.status === 'success') {
                invalidateAllGroups()
                dispatch(changeRoute({ route: 'groups-management' }))
                if (result.response.length > 0) {
                    dispatch(setAllUserRoles(result.response))
                }
                snackbar.show({
                    severity: 'success',
                    text: 'Group deleted successfully!'
                })
            } else if (result.status === 'error') {
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to delete the group. Please try again.'
                })
            }
        }
    })

    const { execute: executeAddMembers, status: statusAddMembers } = useCommand({
        commandFn: async (emails: string[]) => {
            if (!currentRoleUUID) {
                throw new Error('Please create the group first before adding members.')
            }
            return await ApiClient.assignUsersToRole(currentRoleUUID, emails)
        },
        onFinish: result => {
            if (result.status === 'success') {
                invalidateUsers()
                handleCloseAddMembersModal()
                snackbar.show({
                    severity: 'success',
                    text: 'Members added successfully to the group!'
                })
            } else if (result.status === 'error') {
                if (result.errorText.includes('create the group first')) {
                    snackbar.show({
                        severity: 'warning',
                        text: result.errorText
                    })
                    handleCloseAddMembersModal()
                } else {
                    snackbar.show({
                        severity: 'error',
                        text: 'Failed to add members to the group. Please try again.'
                    })
                }
            }
        }
    })

    const { execute: executeRemoveUser, status: statusRemoveUser } = useCommand({
        commandFn: async (userEmail: string) => {
            if (!currentRoleUUID) {
                throw new Error('No role selected')
            }
            return await ApiClient.removeUsersFromRole([userEmail], currentRoleUUID)
        },
        onFinish: result => {
            if (result.status === 'success') {
                invalidateUsers()
                setSelectedUserToRemove(null)
                snackbar.show({
                    severity: 'success',
                    text: 'User removed from group successfully!'
                })
            } else if (result.status === 'error') {
                snackbar.show({
                    severity: 'error',
                    text: 'Failed to remove user from group. Please try again.'
                })
            }
        }
    })

    const { status: statusAllUsersByRoles, response: users } = useQuery<IUser[]>(
        (currentRoleKey ?? group?.key)
            ? {
                    queryKey: buildQueryKey('allUsersByRole' + (currentRoleKey ?? group?.key)),
                    queryFn: () => ApiClient.getUserByRole(currentRoleKey ?? group?.key),
                    cache: true
                }
            : {
                    queryKey: buildQueryKey('allUsersByRole-empty'),
                    queryFn: () => Promise.resolve([]),
                    cache: false
                }
    )

    const { status: allUsersStatus, response: userslist } = useQuery<IUser[]>({
        queryKey: buildQueryKey('allUsers'),
        queryFn: () => UsersManager.getAllUsers(),
        cache: true
    })

    const usernameList = userslist?.map(user => user.email)
    const availableEmails = usernameList?.filter(email => !selectedEmails.includes(email) && !users?.some(user => user.email === email))

    const isDirty = React.useMemo(() => {
        if (!group && !currentRoleUUID) {
            return (
                groupName.trim() !== '' || description.trim() !== '' || allowedEntitlements.length > 0 || selectedEmails.length > 0
            )
        }

        const initial = {
            name: group?.name ?? '',
            description: group?.description ?? '',
            allowedEntitlements: group?.entitlements.allow ?? []
        }

        const current = {
            name: groupName,
            description: description,
            allowedEntitlements: allowedEntitlements
        }

        const hasChangedFields = Helper.stableStringify(initial) !== Helper.stableStringify(current)
        const hasPendingMembers = currentRoleUUID && selectedEmails.length > 0

        return hasChangedFields || hasPendingMembers
    }, [
        groupName,
        description,
        allowedEntitlements,
        selectedEmails,
        group,
        currentRoleUUID
    ])

    const addEntitlements = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>, newValue: string) => {
        if (newValue.trim()) {
            setList([...list, newValue.trim()])
        }
    }

    const removeEntitlements = (list: string[], setList: React.Dispatch<React.SetStateAction<string[]>>, index: number) => {
        const updatedList = [...list]
        updatedList.splice(index, 1)
        setList(updatedList)
    }

    const handleSave = async () => {
        await executeSaveRole({
            groupName,
            description,
            allowedEntitlements,
            selectedEmails
        })
    }

    const handleDelete = async () => {
        if (!isEditMode || !group) {
            return
        }

        modals.openConfirm({
            title: 'Confirm Group Deletion',
            children: <p>Are you sure you want to delete this Group? All the users with this group will lose their entitlements.</p>,
            labels: { confirm: 'Yes, Delete', cancel: 'Cancel' },
            colors: { confirm: 'error', cancel: 'primary' },
            onConfirm: async () => {
                await executeDeleteRole(group.uuid)
            }
        })
    }

    const handleOpenAddMembersModal = () => {
        setIsAddMembersModalOpen(true)
        setSelectedEmails([])
        setCurrentEmailInput('')
    }

    const handleCloseAddMembersModal = () => {
        setIsAddMembersModalOpen(false)
        setCurrentEmailInput('')
    }

    const handleAddEmail = (email: string) => {
        if (email && !selectedEmails.includes(email)) {
            setSelectedEmails([...selectedEmails, email])
            setCurrentEmailInput('')
        }
    }

    const handleRemoveEmail = (emailToRemove: string) => {
        setSelectedEmails(selectedEmails.filter(email => email !== emailToRemove))
    }

    const handleRemoveAllEmails = () => {
        setSelectedEmails([])
    }

    const handleRemoveUser = async () => {
        if (!selectedUserToRemove) {
            return
        }
        await executeRemoveUser(selectedUserToRemove)
    }

    const handleConfirmAddMembers = async () => {
        await executeAddMembers(selectedEmails)
    }

    const isLoading = statusSaveRole === 'loading'
    const isRemovingUser = statusRemoveUser === 'loading'
    const isSavingMembers = statusAddMembers === 'loading'
    const isChangingLoading = statusSaveRole === 'loading' || statusDeleteRole === 'loading' || statusRemoveUser === 'loading'

    return (
        <Stack spacing={2}>
            <SmartCard>
                <Box display='flex' flexDirection='column' gap={8}>
                    <Box display='flex' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
                        <Box display='flex' alignItems='center' gap={1}>
                            <HubIcon />
                            <TextField
                                sx={{
                                    '& input': {
                                        padding: 'var(--unit)'
                                    }
                                }}
                                variant='outlined'
                                value={groupName}
                                placeholder='Group Name'
                                onChange={e => {
                                    setGroupName(e.target.value)
                                }}
                                disabled={isChangingLoading}
                            />
                        </Box>
                        <Divider />
                        <Box display='flex' gap={1}>
                            <LoadingButton
                                variant='contained'
                                color='primary'
                                loading={isLoading}
                                startIcon={(
                                    <Badge
                                        variant='dot'
                                        color='warning'
                                        invisible={!isDirty}>
                                        <Save />
                                    </Badge>
                                )}
                                disabled={!groupName || isChangingLoading}
                                onClick={handleSave}>
                                {isEditMode ? 'SAVE CHANGES' : 'CREATE GROUP'}
                            </LoadingButton>

                            <LoadingButton
                                onClick={handleDelete}
                                variant='contained'
                                color='error'
                                startIcon={<Delete />}
                                loading={statusDeleteRole === 'loading'}
                                disabled={isChangingLoading || isNewRole || (!isEditMode && isClone)}>
                                DELETE GROUP
                            </LoadingButton>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant='h6' mb={2}>
                            Description
                        </Typography>
                        <TextField
                            fullWidth
                            variant='outlined'
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder='Enter group Description'
                            sx={{ mb: 2 }}
                            multiline
                            rows={3}
                            disabled={isChangingLoading}
                        />
                    </Box>

                    <Box display='flex' gap={2}>
                        <Box flex={1}>
                            <Box display='flex' alignItems='center' mb={2} gap={2}>
                                <CheckIcon color='success' />
                                <Typography variant='h6'>
                                    Can Approve
                                </Typography>
                            </Box>
                            <TableContainer component={Paper} sx={{ overflowY: 'auto' }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Entity</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allowedEntitlements.map((action: string, index: number) => (
                                            <TableRow key={index}>
                                                <TableCell>{action}</TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        color='error'
                                                        onClick={() => removeEntitlements(allowedEntitlements, setAllowedEntitlements, index)}
                                                        disabled={isChangingLoading}>
                                                        <Delete />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={2} align='center'>
                                                <Autocomplete
                                                    key={allowedAutocompleteKey}
                                                    isOptionEqualToValue={(o, v) => o === v}
                                                    slotProps={{
                                                        popper: {
                                                            placement: 'top',
                                                            modifiers: [
                                                                {
                                                                    name: 'flip',
                                                                    enabled: false
                                                                }
                                                            ]
                                                        }
                                                    }}
                                                    options={APPROVAL_ENTITLEMENTS.filter(
                                                        option => !allowedEntitlements.includes(option) && !disallowedEntitlements.includes(option)
                                                    )}
                                                    renderInput={params => (
                                                        <TextField {...params} placeholder='Add entitlement' variant='outlined' />
                                                    )}
                                                    onChange={(_, newValue) => {
                                                        if (newValue) {
                                                            addEntitlements(allowedEntitlements, setAllowedEntitlements, newValue)
                                                            setAllowedAutocompleteKey(prev => prev + 1)
                                                        }
                                                    }}
                                                    clearOnBlur
                                                    clearOnEscape
                                                    disabled={isChangingLoading}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                </Box>
            </SmartCard>
            {(isEditMode || currentRoleUUID) && (
                <SmartCard>
                    <Stack spacing={2}>
                        <Stack alignItems='center' direction='row' spacing={2} justifyContent='space-between'>
                            <Stack alignItems='center' direction='row' spacing={2}>
                                <GroupsIcon />
                                <Typography variant='h6'>
                                    Group Members
                                </Typography>
                            </Stack>
                            <Stack direction='row' spacing={1}>
                                {selectedUserToRemove && (
                                    <LoadingButton
                                        variant='contained'
                                        color='error'
                                        startIcon={<PersonRemove />}
                                        onClick={handleRemoveUser}
                                        loading={isRemovingUser}
                                        disabled={isChangingLoading}>
                                        Remove User
                                    </LoadingButton>
                                )}
                                <Button
                                    variant='contained'
                                    startIcon={<PersonAdd />}
                                    onClick={handleOpenAddMembersModal}
                                    disabled={isChangingLoading}>
                                    Add User
                                </Button>
                            </Stack>
                        </Stack>
                        <Stack>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow key='header'>
                                            <TableCell padding='checkbox'>
                                                <Checkbox
                                                    disabled
                                                    sx={{ visibility: 'hidden' }}
                                                />
                                            </TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Last Active</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {statusAllUsersByRoles === 'loading' && (
                                            <TableRow>
                                                <TableCell colSpan={4} align='center'>
                                                    <CircularProgress size={24} />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {statusAllUsersByRoles !== 'loading' && users?.length === 0 && (
                                            <TableRow key='no-users'>
                                                <TableCell colSpan={4} align='center'>
                                                    <Typography variant='body2' color='textSecondary'>
                                                        No Users for this group
                                                    </Typography>
                                                    <Button
                                                        variant='outlined'
                                                        startIcon={<Add />}
                                                        onClick={handleOpenAddMembersModal}
                                                        sx={{ mt: 2 }}
                                                        disabled={isChangingLoading}>
                                                        Add first member
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {statusAllUsersByRoles !== 'loading' && users?.map((user, index) => (
                                            <TableRow key={`${user.id}-${user.email}-${index}`}>
                                                <TableCell padding='checkbox'>
                                                    <Checkbox
                                                        checked={selectedUserToRemove === user.email}
                                                        onChange={e => {
                                                            if (e.target.checked) {
                                                                setSelectedUserToRemove(user.email)
                                                            } else {
                                                                setSelectedUserToRemove(null)
                                                            }
                                                        }}
                                                        disabled={isRemovingUser || isChangingLoading}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box display='flex' alignItems='center' gap={1}>
                                                        <Avatar>{user.name?.charAt(0).toUpperCase()}</Avatar>
                                                        <Box>
                                                            <Typography variant='body1'>{user.name}</Typography>
                                                            <Typography variant='body2' color='textSecondary'>
                                                                {user.email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>3 hours</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </Stack>
                </SmartCard>
            )}
            <Dialog
                open={isAddMembersModalOpen}
                onClose={handleCloseAddMembersModal}
                maxWidth='sm'
                fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h5'>Add members to this group</Typography>
                    <IconButton onClick={handleCloseAddMembersModal}>
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={3}>
                        <Typography variant='body1' color='textSecondary'>
                            Add below the users you want to include in this group.
                        </Typography>

                        <Box>
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                Username or email of members to invite:
                            </Typography>
                            <Autocomplete
                                loading={allUsersStatus === 'loading'}
                                options={availableEmails || []}
                                value={currentEmailInput}
                                onChange={(_, newValue) => {
                                    if (newValue) {
                                        handleAddEmail(newValue)
                                    }
                                }}
                                onInputChange={(_, newInputValue) => {
                                    setCurrentEmailInput(newInputValue)
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder='john.doe@email.com'
                                        variant='outlined'
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#f5f5f5'
                                            }
                                        }}
                                    />
                                )}
                                freeSolo
                                clearOnBlur
                                clearOnEscape
                                disabled={isSavingMembers}
                            />
                        </Box>

                        {selectedEmails.length > 0 && (
                            <Box>
                                <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                                    <Typography variant='body2'>
                                        Send Permission to:
                                    </Typography>
                                    <Button
                                        startIcon={<CloseRoundedIcon />}
                                        variant='text'
                                        color='error'
                                        size='small'
                                        onClick={handleRemoveAllEmails}
                                        sx={{ fontSize: '12px' }}
                                        disabled={isSavingMembers}>
                                        Remove All
                                    </Button>
                                </Box>

                                <Stack spacing={2}>
                                    {selectedEmails.map((email, index) => (
                                        <Box key={`selected-${email}-${index}`} display='flex' alignItems='center' justifyContent='space-between'>
                                            <Box display='flex' alignItems='center' gap={2}>
                                                <Avatar sx={{ width: 32, height: 32, fontSize: '12px' }}>
                                                    {email.substring(0, 2).toUpperCase()}
                                                </Avatar>
                                                <Typography variant='body2'>{email}</Typography>
                                            </Box>
                                            <IconButton
                                                size='small'
                                                onClick={() => handleRemoveEmail(email)}
                                                disabled={isSavingMembers}>
                                                <CloseRoundedIcon fontSize='small' />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <LoadingButton
                        variant='contained'
                        startIcon={<PersonAdd />}
                        onClick={handleConfirmAddMembers}
                        loading={isSavingMembers}
                        disabled={selectedEmails.length === 0}>
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

export default GroupsEditor
